import { useState } from "react";
import { Accommodation } from "@/mocks/accommodations";

interface ReservationModalProps {
  accommodation: Accommodation;
  onClose: () => void;
}

const ReservationModal = ({ accommodation, onClose }: ReservationModalProps) => {
  const [tab, setTab] = useState<"phone" | "form">("phone");
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    checkInDate: "",
    checkOutDate: "",
    guests: "2",
    roomType: "",
    specialRequests: "",
  });
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleCopyPhone = () => {
    if (accommodation.phone) {
      navigator.clipboard.writeText(accommodation.phone);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSending(true);
    const body = new URLSearchParams();
    body.append("businessName", accommodation.businessName);
    body.append("businessPhone", accommodation.phone || "");
    body.append("city", accommodation.city);
    body.append("name", form.name);
    body.append("email", form.email);
    body.append("phone", form.phone);
    body.append("checkInDate", form.checkInDate);
    body.append("checkOutDate", form.checkOutDate);
    body.append("guests", form.guests);
    body.append("roomType", form.roomType);
    body.append("specialRequests", form.specialRequests);
    try {
      await fetch("https://readdy.ai/api/form/d7d5mp9noju8nk5a155g", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
    } catch { /* silent */ }
    setTimeout(() => { setSending(false); setSent(true); }, 1000);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl w-full max-w-lg max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-stone-100">
          <div>
            <h3 className="font-bold text-stone-900 text-base" style={{ fontFamily: "'Playfair Display', serif" }}>
              Rezervasyon Yap
            </h3>
            <p className="text-xs text-stone-400 mt-0.5">{accommodation.businessName} · {accommodation.city}</p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center bg-stone-100 hover:bg-stone-200 rounded-full cursor-pointer transition-colors"
          >
            <i className="ri-close-line text-stone-600"></i>
          </button>
        </div>

        {/* Tab Toggle */}
        <div className="px-6 pt-4">
          <div className="flex items-center bg-stone-100 rounded-xl p-1 gap-1">
            <button
              onClick={() => setTab("phone")}
              className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                tab === "phone" ? "bg-white text-stone-900" : "text-stone-500 hover:text-stone-700"
              }`}
            >
              <i className="ri-phone-line"></i> Telefonla Ara
            </button>
            <button
              onClick={() => setTab("form")}
              className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                tab === "form" ? "bg-white text-stone-900" : "text-stone-500 hover:text-stone-700"
              }`}
            >
              <i className="ri-calendar-check-line"></i> Form ile Rezervasyon
            </button>
          </div>
        </div>

        {/* Phone Tab */}
        {tab === "phone" && (
          <div className="p-6 space-y-4">
            <div className="bg-amber-50 border border-amber-100 rounded-2xl p-5 text-center">
              <div className="w-14 h-14 flex items-center justify-center mx-auto mb-3 bg-amber-100 rounded-full">
                <i className="ri-phone-fill text-amber-600 text-2xl"></i>
              </div>
              <p className="text-xs text-stone-500 mb-2">Rezervasyon & Bilgi Hattı</p>
              {accommodation.phone ? (
                <>
                  <a
                    href={`tel:${accommodation.phone}`}
                    className="block text-2xl font-bold text-stone-900 hover:text-amber-600 transition-colors cursor-pointer"
                    style={{ fontFamily: "'Playfair Display', serif" }}
                  >
                    {accommodation.phone}
                  </a>
                  <p className="text-xs text-stone-400 mt-1 mb-4">Doğrudan arayarak rezervasyon yapabilirsiniz</p>
                  <div className="flex gap-3 justify-center">
                    <a
                      href={`tel:${accommodation.phone}`}
                      className="flex items-center gap-2 px-6 py-3 bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-semibold rounded-xl transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-phone-fill"></i> Hemen Ara
                    </a>
                    <button
                      onClick={handleCopyPhone}
                      className={`flex items-center gap-2 px-4 py-3 border text-sm font-semibold rounded-xl transition-colors cursor-pointer whitespace-nowrap ${
                        copied
                          ? "border-emerald-300 bg-emerald-50 text-emerald-700"
                          : "border-stone-200 text-stone-600 hover:bg-stone-50"
                      }`}
                    >
                      <i className={copied ? "ri-check-line" : "ri-file-copy-line"}></i>
                      {copied ? "Kopyalandı!" : "Kopyala"}
                    </button>
                  </div>
                </>
              ) : (
                <p className="text-stone-400 text-sm">Telefon numarası henüz eklenmemiş.</p>
              )}
            </div>

            {/* WhatsApp if available */}
            {accommodation.phone && (
              <a
                href={`https://wa.me/${accommodation.phone.replace(/\D/g, "").replace(/^0/, "90")}?text=${encodeURIComponent(`Merhaba, ${accommodation.businessName} için rezervasyon hakkında bilgi almak istiyorum.`)}`}
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="flex items-center justify-center gap-3 w-full py-3 bg-[#25D366] hover:bg-[#20bd5a] text-white text-sm font-semibold rounded-xl transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-whatsapp-line text-lg"></i>
                WhatsApp ile Yaz
              </a>
            )}

            {/* Business info */}
            <div className="bg-stone-50 rounded-xl p-4 space-y-2">
              <p className="text-xs font-semibold text-stone-500 uppercase tracking-wider mb-3">İşletme Bilgileri</p>
              <div className="flex items-center gap-2 text-sm text-stone-600">
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                  <i className="ri-map-pin-line text-amber-500 text-sm"></i>
                </div>
                <span>{accommodation.address}</span>
              </div>
              {accommodation.checkIn && (
                <div className="flex items-center gap-2 text-sm text-stone-600">
                  <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                    <i className="ri-time-line text-amber-500 text-sm"></i>
                  </div>
                  <span>Check-in: {accommodation.checkIn} · Check-out: {accommodation.checkOut}</span>
                </div>
              )}
              <div className="flex items-center gap-2 text-sm text-stone-600">
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                  <i className="ri-money-cny-circle-line text-amber-500 text-sm"></i>
                </div>
                <span>{accommodation.priceRange}</span>
              </div>
              {accommodation.website && (
                <a
                  href={accommodation.website}
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className="flex items-center gap-2 text-sm text-amber-600 hover:text-amber-700 cursor-pointer"
                >
                  <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                    <i className="ri-global-line text-amber-500 text-sm"></i>
                  </div>
                  <span>Web Sitesini Ziyaret Et</span>
                  <i className="ri-external-link-line text-xs"></i>
                </a>
              )}
            </div>

            <button
              onClick={() => setTab("form")}
              className="w-full py-2.5 border border-stone-200 text-stone-600 text-sm font-medium rounded-xl hover:bg-stone-50 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-calendar-line mr-1.5"></i>
              Rezervasyon Formu ile Devam Et
            </button>
          </div>
        )}

        {/* Form Tab */}
        {tab === "form" && (
          <div className="p-6">
            {sent ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 bg-emerald-100 rounded-full">
                  <i className="ri-checkbox-circle-fill text-emerald-500 text-3xl"></i>
                </div>
                <h4 className="font-bold text-stone-900 mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                  Rezervasyon Talebiniz Alındı!
                </h4>
                <p className="text-stone-500 text-sm leading-relaxed mb-2">
                  <strong>{accommodation.businessName}</strong> en kısa sürede sizinle iletişime geçecektir.
                </p>
                <p className="text-stone-400 text-xs mb-6">
                  Onay için <strong>{form.email}</strong> adresinizi kontrol edin.
                </p>
                {accommodation.phone && (
                  <a
                    href={`tel:${accommodation.phone}`}
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-emerald-500 text-white text-sm font-semibold rounded-xl hover:bg-emerald-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-phone-fill"></i> Hızlı Onay için Ara: {accommodation.phone}
                  </a>
                )}
                <button
                  onClick={onClose}
                  className="block mx-auto mt-3 text-xs text-stone-400 hover:text-stone-600 cursor-pointer"
                >
                  Kapat
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4" data-readdy-form>
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2">
                    <label className="block text-xs font-semibold text-stone-600 mb-1.5">Adınız Soyadınız *</label>
                    <input
                      type="text"
                      name="name"
                      required
                      value={form.name}
                      onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                      placeholder="Ad Soyad"
                      className="w-full border border-stone-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-amber-400 transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-stone-600 mb-1.5">E-posta *</label>
                    <input
                      type="email"
                      name="email"
                      required
                      value={form.email}
                      onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
                      placeholder="email@ornek.com"
                      className="w-full border border-stone-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-amber-400 transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-stone-600 mb-1.5">Telefon *</label>
                    <input
                      type="tel"
                      name="phone"
                      required
                      value={form.phone}
                      onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))}
                      placeholder="0532 000 00 00"
                      className="w-full border border-stone-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-amber-400 transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-stone-600 mb-1.5">Giriş Tarihi *</label>
                    <input
                      type="date"
                      name="checkInDate"
                      required
                      value={form.checkInDate}
                      onChange={(e) => setForm((p) => ({ ...p, checkInDate: e.target.value }))}
                      className="w-full border border-stone-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-amber-400 transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-stone-600 mb-1.5">Çıkış Tarihi *</label>
                    <input
                      type="date"
                      name="checkOutDate"
                      required
                      value={form.checkOutDate}
                      onChange={(e) => setForm((p) => ({ ...p, checkOutDate: e.target.value }))}
                      className="w-full border border-stone-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-amber-400 transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-stone-600 mb-1.5">Misafir Sayısı</label>
                    <select
                      name="guests"
                      value={form.guests}
                      onChange={(e) => setForm((p) => ({ ...p, guests: e.target.value }))}
                      className="w-full border border-stone-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-amber-400 bg-white"
                    >
                      {["1", "2", "3", "4", "5", "6+"].map((n) => (
                        <option key={n} value={n}>{n} Kişi</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-stone-600 mb-1.5">Oda Tipi</label>
                    <select
                      name="roomType"
                      value={form.roomType}
                      onChange={(e) => setForm((p) => ({ ...p, roomType: e.target.value }))}
                      className="w-full border border-stone-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-amber-400 bg-white"
                    >
                      <option value="">Seçin (isteğe bağlı)</option>
                      <option value="Standart Oda">Standart Oda</option>
                      <option value="Deluxe Oda">Deluxe Oda</option>
                      <option value="Suite">Suite</option>
                      <option value="Aile Odası">Aile Odası</option>
                      <option value="Deniz Manzaralı">Deniz Manzaralı</option>
                      <option value="Bungalov / Çadır">Bungalov / Çadır</option>
                    </select>
                  </div>
                  <div className="col-span-2">
                    <label className="block text-xs font-semibold text-stone-600 mb-1.5">Özel İstekler</label>
                    <textarea
                      name="specialRequests"
                      rows={2}
                      maxLength={500}
                      value={form.specialRequests}
                      onChange={(e) => setForm((p) => ({ ...p, specialRequests: e.target.value }))}
                      placeholder="Erken check-in, bebek karyolası, diyet tercihleri vb."
                      className="w-full border border-stone-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-amber-400 transition-colors resize-none"
                    />
                    <p className="text-xs text-stone-400 text-right mt-1">{form.specialRequests.length}/500</p>
                  </div>
                </div>

                {/* Phone quick access */}
                {accommodation.phone && (
                  <div className="flex items-center gap-3 bg-stone-50 rounded-xl p-3">
                    <div className="w-8 h-8 flex items-center justify-center bg-emerald-100 rounded-lg flex-shrink-0">
                      <i className="ri-phone-line text-emerald-600 text-sm"></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-stone-500">Hızlı onay için arayın</p>
                      <a href={`tel:${accommodation.phone}`} className="text-sm font-bold text-stone-900 hover:text-amber-600 transition-colors cursor-pointer">
                        {accommodation.phone}
                      </a>
                    </div>
                    <a
                      href={`tel:${accommodation.phone}`}
                      className="flex items-center gap-1 px-3 py-1.5 bg-emerald-500 text-white text-xs font-semibold rounded-lg hover:bg-emerald-600 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-phone-fill"></i> Ara
                    </a>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={sending}
                  className="w-full flex items-center justify-center gap-2 py-3 bg-amber-500 hover:bg-amber-600 disabled:opacity-60 text-white text-sm font-semibold rounded-xl transition-colors cursor-pointer whitespace-nowrap"
                >
                  {sending
                    ? <><i className="ri-loader-4-line animate-spin"></i> Gönderiliyor...</>
                    : <><i className="ri-calendar-check-line"></i> Rezervasyon Talebi Gönder</>}
                </button>
              </form>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ReservationModal;
